import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Stack, TextField, Typography, Button, Divider, InputAdornment, IconButton, CircularProgress, Alert, AlertTitle,Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Header } from 'components/Header';
import { useNavigate } from 'react-router-dom';
import { updateStatus, StatusIds } from '../services/api';
import { formatApiErrorMessage, logApiError } from '../services/errors';
import { Scanner as QrScannerComp } from '@yudiel/react-qr-scanner';
import { LoadingButton } from '@mui/lab';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';

interface ScanDeliveryPageProps {
  externalIdentity: string;
  associateId: string;
  associateEmail: string;
  associateName?: string;
  storeId?: string;
}

const ScanDeliveryPage: React.FC<ScanDeliveryPageProps> = ({ externalIdentity, associateId, associateEmail, associateName,storeId}) => {
  const navigate = useNavigate();
  const [deliveryId, setDeliveryId] = useState('');
  // no need for service item id input; backend accepts deliveryId for status update
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cameraHint, setCameraHint] = useState<string | null>(null);
  const isSecureContext =
    typeof window !== 'undefined' &&
    (window.isSecureContext || ['localhost', '127.0.0.1'].includes(window.location.hostname));
  const [lastScannedId, setLastScannedId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(true);
  const [scannerKey, setScannerKey] = useState(0);
  const scannerContainerRef = useRef<HTMLDivElement | null>(null);
  type ItemStatus = 'pending' | 'submitting' | 'success' | 'error';
  const [items, setItems] = useState<{ id: string; status: ItemStatus; errorMessage?: string }[]>([]);
  const [submitSummary, setSubmitSummary] = useState<{ total: number; success: number; failed: number } | null>(null);
  const [misdirectedDialog, setMisdirectedDialog] = useState({
  open: false,
  title: '',
  deliveryId: '',
  expectedStore: '',
  });
  const resolvedAssociateName = useMemo(
    () => (associateName?.trim() || associateEmail || associateId),
    [associateId, associateEmail, associateName]
  );

  const onError = useCallback((err: any) => {
    console.error(err);
    const name = err?.name || '';
    const message = err?.message || '';
    // On iOS WebView, closing camera often raises Abort/NotAllowed/NotReadable.
    if (
      /NotAllowedError|AbortError|NotReadableError|OverconstrainedError/i.test(name) ||
      /camera|permission|aborted|ended/i.test(message)
    ) {
      setCameraHint('Camera closed. Tap Scan Again to reopen.');
      setError(null);
      setScanning(false); // replace video with Scan Again button
      setLastScannedId(null);
    }
  }, []);

  // Detect when the underlying video stream stops (e.g., user closes camera in iOS WebView)
  useEffect(() => {
    if (!scanning) return;
    const node = scannerContainerRef.current;
    if (!node) return;
    const video: HTMLVideoElement | null = node.querySelector('video');
    if (!video) return;

    const handleEnded = () => {
      // If camera closed without a successful scan, fall back to Scan Again button
      if (scanning && !lastScannedId) {
        setScanning(false);
        setCameraHint('Camera closed. Tap Scan Again to reopen.');
        setError(null);
        setLastScannedId(null);
      }
    };

    video.addEventListener('pause', handleEnded);
    video.addEventListener('ended', handleEnded);
    // iOS WebKit specific event when leaving fullscreen
    (video as any).addEventListener?.('webkitendfullscreen', handleEnded);

    return () => {
      video.removeEventListener('pause', handleEnded);
      video.removeEventListener('ended', handleEnded);
      (video as any).removeEventListener?.('webkitendfullscreen', handleEnded);
    };
  }, [scanning, lastScannedId]);

  // Use direct scanner component; the library handles permission prompts.

  const addToList = (raw: string) => {
    const id = (raw || '').trim();
    if (!id) return;
    setError(null);
    setCameraHint(null);
    setItems((prev) => (prev.some((x) => x.id === id) ? prev : [...prev, { id, status: 'pending' }]));
    setDeliveryId('');
  };

  const handleMarkReceived = async () => {
    setError(null);
    setSubmitSummary(null);
    // if user typed but didn't add to list, include it
    const extra = deliveryId.trim();

    const isValidDeliveryId = (value: string) => /^\d+$/.test(value);
    //Collect all deliveryIDs to validate
    const allIds = [...items.map((x) => x.id), ...(extra ? [extra] : [])];
    // Validate: only numbers allowed
    const invalidId = allIds.find((id) => !isValidDeliveryId(id));
    if (invalidId) {
      setError('Delivery ID must be numeric (numbers only)');
      return;
    }
    if (extra && !items.some((x) => x.id === extra)) {
      setItems((prev) => [...prev, { id: extra, status: 'pending' }]);
    }
    if (items.length === 0 && !extra) {
      setError('Add at least one Delivery ID.');
      return;
    }
    try {
      setSubmitting(true);
      // build processing queue: items not yet successful + optional extra
      const current = [...items]; // snapshot
      const queueIds: string[] = current.filter((x) => x.status !== 'success').map((x) => x.id);
      if (extra && !queueIds.includes(extra)) queueIds.push(extra);
      let successCount = 0;
      let failedCount = 0;

      for (const id of queueIds) {
        setItems((prev) => {
          // ensure item exists
          const exists = prev.some((x) => x.id === id);
          const base = exists ? prev : [...prev, { id, status: 'pending' as ItemStatus }];
          return base.map((x) => (x.id === id ? { ...x, status: 'submitting', errorMessage: undefined } : x));
        });
        try {
          await updateStatus(externalIdentity, {
            status: StatusIds.READY_TO_PICKUP,
            associateId,
            associateName: resolvedAssociateName,
            associateEmail,
            deliveryId: id,
            storeId
          });
          successCount += 1;
          setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: 'success', errorMessage: undefined } : x)));
        } catch (e: any) {
          failedCount += 1;
          logApiError(`Marking delivery ${id} as received`, e);
          const responseData = e?.response?.data;
          const statusCode = e?.response?.status;
          // Handle misdirected shipment validation
          if (
            statusCode === 409 &&
            responseData?.errorCode === 'MISDIRECTED_SHIPMENT'
          ) {
            const { deliveryId, expectedStore } =
            responseData;

            setMisdirectedDialog({
              open: true,
              title: 'Misdirected Shipment',
              deliveryId,
              expectedStore,
            });

            setItems((prev) =>
              prev.map((x) =>
                x.id === id
                  ? {
                      ...x,
                      status: 'error',
                      errorMessage: `This shipment is for Store ${expectedStore}. Please see Shipping and Receiving P&P item (9) for instructions.`,
                    }
                  : x
              )
            );
            continue;
          }
          const message = formatApiErrorMessage(e, 'Failed to submit');
          setItems((prev) => prev.map((x) => (x.id === id ? { ...x, status: 'error', errorMessage: message } : x)));
        }
      }

      // compute summary
      setSubmitSummary({ total: queueIds.length, success: successCount, failed: failedCount });
    } catch (err: any) {
      logApiError('Submitting delivery IDs', err);
      setError(formatApiErrorMessage(err, 'Failed to submit one or more Delivery IDs'));
    } finally {
      setSubmitting(false);
    }
  };

  // no order preview/status; we just capture Delivery ID and submit
  const handleInputChange = (value: string) => {
    setDeliveryId(value);
    const trimmed = value.trim();
    // Clear error if input becomes valid
    if (!trimmed || /^\d+$/.test(trimmed)) {
      setError(null);
    }
  };
  const removeItem = (id: string) => {
  setItems((prev) => {
    const updated = prev.filter((x) => x.id !== id);

    // Re-validate remaining items
    const hasInvalid = updated.some((x) => !/^\d+$/.test(x.id));

    if (!hasInvalid) {
      setError(null);
    }

    return updated;
  });
};
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Header title="Scan Delivery ID" showBackButton onBack={() => navigate(-1)} sticky elevateOnScroll align="left" />

      <Paper
        elevation={0}
        sx={(theme) => ({
          mx: 2,
          mt: 2,
          p: 2,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          background: alpha(theme.palette.primary.light, 0.06),
        })}
      >
        <Stack spacing={2}>
          {
            <>
              {isSecureContext ? (
                scanning ? (
                  <Box ref={scannerContainerRef} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                    <QrScannerComp
                      key={scannerKey}
                      onScan={async (codes: any[]) => {
                        const text = Array.isArray(codes) && codes[0]?.rawValue ? codes[0].rawValue : (codes?.[0]?.rawValue || codes?.[0]?.rawValueText || '');
                        if (!text || lastScannedId === text) return;
                        addToList(text);
                        setError(null);
                        setCameraHint(null);
                        setLastScannedId(text);
                        setScanning(false); // close scanner; user can tap Scan Again to continue
                      }}
                      onError={(err: any) => onError(err)}
                      constraints={{ facingMode: 'environment' }}
                      styles={{ video: { width: '100%' } }}
                      formats={['qr_code', 'code_128', 'code_39', 'ean_13', 'ean_8', 'pdf417']}
                    />
                  </Box>
                ) : (
                  <Stack spacing={1} alignItems="stretch">
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        // reset scanner in place
                        setScanning(true);
                        setScannerKey((k) => k + 1);
                        setDeliveryId('');
                        setLastScannedId(null);
                        setError(null);
                        setCameraHint(null);
                      }}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                    >
                      Scan Again
                    </Button>
                    {cameraHint && (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                        {cameraHint}
                      </Typography>
                    )}
                  </Stack>
                )
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Camera access requires HTTPS or localhost. Please open the app over HTTPS (or localhost) and try again, or enter the delivery ID manually below.
                </Typography>
              )}

              <Divider textAlign="center">or</Divider>
              <TextField
                label="Delivery ID"
                value={deliveryId}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addToList(deliveryId);
                  }
                }}
                fullWidth
                placeholder="Scan or paste delivery ID"
                helperText={deliveryId ? 'Delivery ID captured' : ''}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCode2OutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Add"
                        onClick={() => addToList(deliveryId)}
                        disabled={!deliveryId.trim()}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={(theme) => ({
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    transition: 'box-shadow .2s ease',
                    '&:hover': { boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.08)}` },
                    '&.Mui-focused': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.16)}` },
                  },
                })}
              />
              {!!items.length && (
                <Paper
                  variant="outlined"
                  sx={(theme) => ({
                    mt: 1,
                    p: 1,
                    borderRadius: 2,
                    borderColor: alpha(theme.palette.divider, 0.9),
                  })}
                >
                  <Stack spacing={1.25} divider={<Divider />}>
                    {items.map((it) => (
                      <Stack key={it.id} direction="row" alignItems="center" justifyContent="space-between">
                        <Stack spacing={0.25} sx={{ mr: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ opacity: it.status === 'success' ? 0.65 : 1 }}>{it.id}</Typography>
                          {it.status === 'error' && it.errorMessage && (
                            <Typography variant="caption" color="error.main">{it.errorMessage}</Typography>
                          )}
                        </Stack>
                        {it.status === 'submitting' && <CircularProgress size={18} />}
                        {it.status === 'success' && <CheckCircleIcon sx={{ color: 'success.main' }} fontSize="small" />}
                        {it.status === 'error' && (
                          <IconButton
                            size="small"
                            aria-label="Remove failed"
                            onClick={() => removeItem(it.id)}
                            disabled={submitting}
                          >
                            <CloseIcon sx={{ color: 'error.main' }} fontSize="small" />
                          </IconButton>
                        )}
                        {it.status === 'pending' && (
                          <IconButton size="small" aria-label="Remove" onClick={() => removeItem(it.id)} disabled={submitting}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              )}
              {/* Scan Again moved to scanner area above */}
              {error && (
                <Typography variant="body2" color="error">
                  {error}
                </Typography>
              )}
            </>
          }

          <Typography variant="caption" color="text.secondary">
            Scan the package label or enter the delivery ID, then confirm below.
          </Typography>
        </Stack>
      </Paper>

      {/* Bottom action (always visible like other pages) */}
      <Box mt={3} mx={2} mb={4}>
        <LoadingButton
          variant="contained"
          fullWidth
          onClick={handleMarkReceived}
          loading={submitting}
          loadingPosition="start"
          startIcon={<CheckCircleIcon />}
          disabled={(items.length === 0 && !deliveryId.trim()) || submitting}
          sx={(theme) => ({
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            '&.Mui-disabled': {
              color: theme.palette.common.white,
              backgroundColor: alpha(theme.palette.primary.main, 0.35),
            },
          })}
        >
          Submit All
        </LoadingButton>
      </Box>

      {submitSummary && (
        <Box mx={2} mb={4}>
          <Alert severity={submitSummary.failed ? 'warning' : 'success'} variant="outlined">
            <AlertTitle>Submission Summary</AlertTitle>
            <Stack direction="row" spacing={2}>
              <Typography variant="body2">Total: {submitSummary.total}</Typography>
              <Typography variant="body2" color="success.main">Success: {submitSummary.success}</Typography>
              <Typography variant="body2" color={submitSummary.failed ? 'error.main' : 'text.secondary'}>Failed: {submitSummary.failed}</Typography>
            </Stack>
            <Stack direction="row" spacing={1} mt={1}>
              <Button size="small" variant="outlined" onClick={() => { setScanning(true); setScannerKey((k) => k + 1); }}>Scan More</Button>
              {!!items.some(i => i.status === 'error') && (
                <Button size="small" variant="contained" color="warning" onClick={() => handleMarkReceived()} disabled={submitting}>Retry Failed</Button>
              )}
              {!!items.some(i => i.status === 'success') && (
                <Button size="small" variant="text" onClick={() => setItems(prev => prev.filter(i => i.status !== 'success'))}>Clear Success</Button>
              )}
            </Stack>
          </Alert>
        </Box>
      )}
      <Dialog
        open={misdirectedDialog.open}
        onClose={() =>
          setMisdirectedDialog({
            open: false,
            title: '',
            deliveryId: '',
            expectedStore:''
          })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{misdirectedDialog.title}</DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ whiteSpace: 'pre-line' }}
          >
            These delivery(s) was not meant to be received at this store.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please notify DCManagement and ship the item(s), clearly marked as
            misdirected, back to the DC in a benson box.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Please close this dialog box for more details.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'flex-end', pr: 2, pb: 2 }}>
          <Button
            onClick={() =>
              setMisdirectedDialog({
                open: false,
                title: '',
                deliveryId: '',
                expectedStore: '',
              })
            }
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScanDeliveryPage;
